/**
 * WASSER E-commerce Platform - Network Status Handler
 *
 * Глобальный обработчик сетевого статуса с toast уведомлениями.
 * Отслеживает online/offline состояние и показывает уведомления.
 *
 * @example
 * // В корневом App.jsx
 * import NetworkStatusHandler from '../components/common/NetworkStatusHandler';
 *
 * function App() {
 *   return (
 *     <>
 *       <NetworkStatusHandler />
 *       <Routes>...</Routes>
 *     </>
 *   );
 * }
 */
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import {
  Alert,
  Box,
  IconButton,
  Slide,
  Snackbar,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Transition component для Snackbar
 */
const SlideTransition = (props) => {
  return <Slide {...props} direction="up" />;
};

/**
 * NetworkStatusHandler Component
 *
 * @param {Object} props
 * @param {number} [props.checkInterval=30000] - Интервал проверки сети (мс)
 * @param {string} [props.testUrl='/api/health'] - URL для проверки соединения
 * @param {boolean} [props.showOnlineNotification=true] - Показывать уведомление при восстановлении
 * @param {number} [props.autoHideDuration=6000] - Время автоскрытия (мс)
 */
const NetworkStatusHandler = ({
  checkInterval = 30000,
  testUrl = '/api/health',
  showOnlineNotification = true,
  autoHideDuration = 6000,
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineBar, setShowOfflineBar] = useState(false);
  const [showOnlineSnackbar, setShowOnlineSnackbar] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const wasOfflineRef = useRef(false);
  const checkTimeoutRef = useRef(null);

  /**
   * Проверка реального соединения через API
   */
  const checkConnection = useCallback(async () => {
    if (isChecking) return;

    setIsChecking(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(testUrl, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        if (!isOnline || wasOfflineRef.current) {
          setIsOnline(true);
          setShowOfflineBar(false);
          if (wasOfflineRef.current && showOnlineNotification) {
            setShowOnlineSnackbar(true);
          }
          wasOfflineRef.current = false;
        }
      } else {
        throw new Error('Server error');
      }
    } catch {
      // Только если браузер тоже offline
      if (!navigator.onLine) {
        setIsOnline(false);
        setShowOfflineBar(true);
        wasOfflineRef.current = true;
      }
    } finally {
      setIsChecking(false);
    }
  }, [isOnline, testUrl, showOnlineNotification, isChecking]);

  /**
   * Обработчик события online
   */
  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setShowOfflineBar(false);
    if (wasOfflineRef.current && showOnlineNotification) {
      setShowOnlineSnackbar(true);
    }
    wasOfflineRef.current = false;
  }, [showOnlineNotification]);

  /**
   * Обработчик события offline
   */
  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setShowOfflineBar(true);
    wasOfflineRef.current = true;
  }, []);

  /**
   * Ручной retry
   */
  const handleRetry = useCallback(() => {
    checkConnection();
  }, [checkConnection]);

  // Подписка на события online/offline
  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Начальная проверка
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  // Периодическая проверка соединения
  useEffect(() => {
    if (checkInterval > 0) {
      const intervalId = setInterval(() => {
        if (!navigator.onLine) {
          checkConnection();
        }
      }, checkInterval);

      return () => clearInterval(intervalId);
    }
  }, [checkInterval, checkConnection]);

  // Cleanup
  useEffect(() => {
    const timeoutId = checkTimeoutRef.current;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <>
      {/* Persistent Offline Banner */}
      <Snackbar
        open={showOfflineBar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          severity="warning"
          variant="filled"
          icon={<WifiOffIcon />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleRetry}
                disabled={isChecking}
                aria-label="Повторить попытку"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <RefreshIcon
                  sx={{
                    animation: isChecking ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setShowOfflineBar(false)}
                aria-label="Закрыть"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          }
          sx={{
            width: '100%',
            maxWidth: 500,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              Нет подключения к интернету
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Проверьте соединение и попробуйте снова
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Online Restored Notification */}
      <Snackbar
        open={showOnlineSnackbar}
        autoHideDuration={autoHideDuration}
        onClose={() => setShowOnlineSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          severity="success"
          variant="filled"
          icon={<WifiIcon />}
          onClose={() => setShowOnlineSnackbar(false)}
          sx={{ minWidth: 280 }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Соединение восстановлено
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default NetworkStatusHandler;
